import { Component } from '@theme/component';

/**
 * Zager video FAQ facade.
 *
 * Ported from `template-parts/video-section.php` + `javascripts/scripts.js:363-395`
 * (see docs/final-blocks-build-sheet.md §2.10). Clicking a link in the left
 * list swaps the right-hand facade (thumbnail + YouTube id) in place — no
 * navigation, no lightbox.
 *
 * This is deliberately a SEPARATE component from `zager-hero-video.js`
 * rather than an extension of it: the hero component's whole job is a
 * `<dialog>` lightbox (open/close, scroll lock, focus return) triggered by a
 * single button. Here there is no dialog at all — the facade is swapped
 * in-place by clicking any of 22 list links, and only the currently-selected
 * video ever gets an iframe. Bolting an inline-swap mode onto the dialog
 * component would mean every consumer of the shared class carrying dead
 * dialog machinery it never uses. The facade-to-iframe swap-on-click (the
 * one piece of real behavioural overlap) is intentionally the same recipe as
 * `zager-hero-video.js`: build the iframe lazily, only on click.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} facade - The clickable facade, carries `data-video-id`.
 * @property {HTMLSourceElement} [source] - Legacy `<picture>` webp `<source>`; absent since 2026-09-12.
 * @property {HTMLImageElement} img - The `<picture>` jpg fallback `<img>`.
 * @property {HTMLButtonElement} playButton - Plays the current facade video.
 * @property {HTMLDivElement} frame - Empty container the iframe is injected into.
 * @property {HTMLAnchorElement[]} link - The 22 scrolling list links.
 *
 * @extends {Component<Refs>}
 */
class ZagerVideoFaqComponent extends Component {
  // `source` is deliberately NOT required — the <picture>/<source> markup was
  // removed on 2026-09-12 and `selectVideo` already guards its absence.
  requiredRefs = ['facade', 'img', 'playButton', 'frame'];

  /**
   * Swaps the facade to the video behind the clicked list link. Stops any
   * currently-playing iframe (the previous video should not keep playing
   * once its thumbnail is gone).
   *
   * @param {Event} event - The click event, retargeted to the link.
   */
  selectVideo(event) {
    const link = /** @type {HTMLAnchorElement} */ (event.target);
    const videoId = link.getAttribute('data-youtube-id');

    if (!videoId) return;

    event.preventDefault();

    const { facade, source, img, frame, link: links } = this.refs;
    const label = link.getAttribute('data-label') || link.textContent?.trim() || '';

    frame.replaceChildren();
    facade.removeAttribute('data-playing');
    facade.setAttribute('data-video-id', videoId);
    // No WebP. YouTube does not have a WebP still for every video — measured on
    // this very list, `G7S1yXCApBw` ("Choosing the right size of your Zager
    // Guitar") serves `hqdefault.jpg` and 404s `vi_webp/.../hqdefault.webp`.
    //
    // `<picture>` falls back on TYPE and MEDIA mismatches, never on a failed
    // download: once the browser has committed to the WebP <source>, a 404
    // leaves the box blank instead of trying the <img>. That is the blank video
    // reported against the Best Sellers strip.
    //
    // An `img.onerror` that dropped the <source> was tried and did not fire
    // reliably. The whole <picture>/<source> is gone from the markup as of
    // 2026-09-12 — keeping an EMPTY <source> stopped the <img> resolving at
    // all and painted the facade black (see the note in the section). The ref
    // is therefore optional; this guard is what lets the component keep
    // working against either shape.
    source?.removeAttribute?.('srcset');
    img.src = `https://i.ytimg.com/vi/${encodeURIComponent(videoId)}/hqdefault.jpg`;
    img.alt = label;

    const linkList = Array.isArray(links) ? links : [links];
    for (const item of linkList) {
      item.classList.toggle('zager-video-faq__link--active', item === link);
    }
  }

  /**
   * Loads and plays the video currently shown in the facade. The iframe is
   * only created here, on click — nothing loads on page view or on a list
   * click alone.
   */
  play() {
    const { facade, frame } = this.refs;
    const videoId = facade.getAttribute('data-video-id');

    if (!videoId || frame.hasChildNodes()) return;

    const iframe = document.createElement('iframe');

    iframe.src = `https://www.youtube-nocookie.com/embed/${encodeURIComponent(videoId)}?autoplay=1&rel=0`;
    iframe.title = facade.querySelector('img')?.alt || 'Video';
    iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
    iframe.allowFullscreen = true;
    iframe.loading = 'lazy';

    frame.append(iframe);
    facade.setAttribute('data-playing', '');
  }
}

if (!customElements.get('zager-video-faq-component')) {
  customElements.define('zager-video-faq-component', ZagerVideoFaqComponent);
}
